import { useState, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import SelectGroup from "../FormComponent/SelectGroup";
import Modal from "../Modal";
import Button from "../Button";

const CSVExport = ({
  data,
  fields,
  filename = "export",
  selectedRows = [],
  idKey,
  onError = () => {},
  onBatchReactivate = null,
  isBatchReactivating = false
}) => {
  const getNestedValue = (obj, path) => {
    if (path === "full_name") {
      const firstName = obj.first_name || "";
      const lastName = obj.last_name || "";

      return `${firstName} ${lastName}`.trim();
    }
    if (path === "teacher_name") {
      const firstName = obj.class_teacher?.first_name || "";
      const lastName = obj.class_teacher?.last_name || "";

      return `${firstName} ${lastName}`.trim();
    }
    if (path === "student_name") {
      const firstName = obj.student?.first_name || "";
      const lastName = obj.student?.last_name || "";

      return `${firstName} ${lastName}`.trim();
    }

    // eslint-disable-next-line no-undefined
    return path.split(".").reduce((o, p) => (o ? o[p] : undefined), obj);
  };

  const convertToCSV = useCallback((objArray, fieldConfigs) => {
    try {
      if (!objArray || !objArray.length) {
        throw new Error("No data to export");
      }

      if (!fieldConfigs || !fieldConfigs.length) {
        throw new Error("No fields configured for export");
      }

      const headerRow = `${fieldConfigs.map(c => c.header).join(",")}\n`;

      const dataRows = objArray.map(row => {
        return fieldConfigs.map(config => {
          const value = getNestedValue(row, config.path);
          // eslint-disable-next-line no-undefined
          let strValue = value === null || value === undefined ? "null" : String(value);
          if (strValue.includes(",") || strValue.includes('"')) {
            strValue = `"${strValue.replace(/"/g, '""')}"`;
          }

          return strValue;
        }).join(",");
      }).join("\n");

      return headerRow + dataRows;
    } catch (error) {
      onError(error);

      return null;
    }
  }, [onError]);

  const [exportModal, setExportModal] = useState(false);
  const [reactivateModal, setReactivateModal] = useState(false);
  const selectRef = useRef(null);

  const downloadCSV = useCallback(() => {
    try {
      // Filter data to only include selected rows
      const dataToExport = selectedRows.length > 0
        ? data.filter(item => selectedRows.includes(String(item[idKey])))
        : [];

      if (dataToExport.length === 0) {
        throw new Error("No rows selected for export");
      }

      const csvData = convertToCSV(dataToExport, fields);
      if (!csvData) return;

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Export completed successfully");
    } catch (error) {
      toast.error(error.message || "Failed to export data");
      onError(error);
    }
  }, [data, filename, fields, convertToCSV, onError, selectedRows]);

  const [selectKey, setSelectKey] = useState(Date.now());

  const handleActionChange = (e) => {
    const action = e.target.value;

    if (action === "export") {
      setExportModal(true);
    } else if (action === "reactivate" && onBatchReactivate) {
      setReactivateModal(true);
    }

    // Reset select value
    e.target.value = "";
  };

  const resetSelect = () => {
    setSelectKey(Date.now());
    if (selectRef.current) {
      selectRef.current.value = "";
    }
  };

  const handleExport = () => {
    downloadCSV();
    setExportModal(false);
    resetSelect();
  };

  const handleReactivate = () => {
    if (onBatchReactivate) {
      onBatchReactivate();
    }
    setReactivateModal(false);
    resetSelect();
  };

  const handleExportModal = () => {
    setExportModal(!exportModal);
    if (!exportModal) {
      resetSelect();
    }
  };

  const handleReactivateModal = () => {
    setReactivateModal(!reactivateModal);
    if (!reactivateModal) {
      resetSelect();
    }
  };

  if (selectedRows.length === 0) {
    return null;
  }

  return (
    <>
      {/* Export Modal */}
      <Modal
        title="Export Data"
        subtitle={
          <div>
            Are you sure you want to export {selectedRows.length > 0
              ? `(${selectedRows.length}) selected data`
              : "all data"}?
          </div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Export Data"
              onClick={handleExport}
            />
          </>
        }
        isopen={exportModal}
        closeModal={handleExportModal}
      />

      {/* Reactivate Modal */}
      <Modal
        title="Batch Reactivate Employees"
        subtitle={
          <div>
            Are you sure you want to reactivate <span className="modal-name-highlight">{selectedRows.length}</span> employee(s)?
            If re-activated, these employees will have access to this platform.
          </div>
        }
        modalContent={<></>}
        modalFooter={
          <>
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName={`Reactivate ${selectedRows.length} Employee(s)`}
              onClick={handleReactivate}
              isLoading={isBatchReactivating}
            />
          </>
        }
        isopen={reactivateModal}
        closeModal={handleReactivateModal}
      />

      <SelectGroup
        children={
          <select
            name="batchActions"
            ref={selectRef}
            key={selectKey}
            defaultValue=""
            onChange={handleActionChange}
            style={{ minWidth: "200px" }}
          >
            <option value="" disabled>Batch Actions ({selectedRows.length})</option>
            <option value="export">Export Selected</option>
            {onBatchReactivate && <option value="reactivate">Reactivate Selected</option>}
          </select>
        }
      />
    </>
  );
};

export default CSVExport;
